import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@kognitos/lattice';
import {
  useUserPreferencesStore,
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
} from '@/shared/stores';
import {
  getRetrieveWorkspacePollInstancesQueryKey,
  useAddWorkspaceBookConnectionPollInstance,
  useSetAutomationsForPollInstance,
} from '@/generated/hooks/bdk/v2/services/library';
import {
  getListAutomationTriggersQueryKey,
  useCreateAutomationTrigger,
} from '@/generated/hooks/grimoire/v1/automation_phase';
import {
  V1PhaseType,
  CreateAutomationTriggerStage,
} from '@/generated/hooks/grimoire/models';
import { captureException } from '@/modules/sentry';
import type {
  ICreateTriggerData,
  IUseCreateTriggerProps,
  IUseCreateTriggerReturn,
  TSentryTags,
} from './types';

/**
 * Generic hook for creating poll triggers.
 *
 * Encapsulates the common logic for creating a poll trigger by:
 * 1. Creating a trigger instance in BDK
 * 2. Setting automations for the trigger instance in BDK
 * 3. Creating an automation trigger in Grimoire with input mappings
 *
 * @param props - Hook props
 * @returns Object containing createTrigger function and isLoading state
 *
 * @example
 * const { createTrigger, isLoading } = useCreateTrigger({
 *   automationId: 'automation-123',
 *   onSuccess: () => closeDialog(),
 *   toastMessages: {
 *     success: t('pollDialog.toast.success'),
 *     error: t('pollDialog.toast.error'),
 *     errorNoTriggerId: t('pollDialog.toast.errorNoTriggerId'),
 *   },
 * });
 *
 */
export function useCreateTrigger({
  automationId,
  isDraft = true,
  onSuccess,
  toastMessages,
  sentryTags,
}: IUseCreateTriggerProps): IUseCreateTriggerReturn {
  // Get organization and workspace IDs for API calls
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  // Query client for cache invalidation
  const queryClient = useQueryClient();

  // Mutation to create poll instance in BDK
  const { mutateAsync: addPollInstance, isPending: isAddingPollInstance } =
    useAddWorkspaceBookConnectionPollInstance();

  // Mutation to set automations for poll instance in BDK
  const {
    mutateAsync: setAutomationsForPollInstance,
    isPending: isSettingAutomations,
  } = useSetAutomationsForPollInstance();

  // Mutation to create automation trigger in Grimoire (equivalent to poll)
  const {
    mutateAsync: createAutomationTrigger,
    isPending: isCreatingAutomationTrigger,
  } = useCreateAutomationTrigger();

  /**
   * Creates a trigger by:
   * 1. Creating trigger instance in BDK
   * 2. Setting automations for the trigger instance
   * 3. Creating automation trigger in Grimoire
   *
   * @param data - Trigger creation data
   */
  const createTrigger = useCallback(
    async (data: ICreateTriggerData) => {
      const {
        bookName,
        bookVersion,
        connectionId,
        pollId,
        pollIntervalMinutes,
        configuration,
        filterExpression,
        inputMappings,
      } = data;

      // Validate required fields
      if (!bookName || !bookVersion || !connectionId || !pollId) {
        return;
      }

      try {
        // Step 1: Create trigger instance in BDK
        const bdkResponse = await addPollInstance({
          organizationId,
          workspaceId,
          bookName,
          bookVersion,
          connectionId,
          data: {
            poll_id: pollId,
            poll_interval_minutes: pollIntervalMinutes,
            configuration,
            filter_expression: filterExpression,
            automation_ids: [automationId],
          },
        });

        // Get the poll instance ID from BDK response
        const pollInstanceId = bdkResponse.data.poll_instance?.id;

        if (!pollInstanceId) {
          toast.error(toastMessages.errorNoTriggerId);
          return;
        }

        // Step 2: Set automations for the poll instance in BDK
        await setAutomationsForPollInstance({
          organizationId,
          workspaceId,
          pollInstanceId,
          data: {
            automation_ids: [automationId],
            connection_id: connectionId,
          },
        });

        // Step 3: Create automation trigger in Grimoire with the mapping
        await createAutomationTrigger({
          organization: organizationId,
          workspace: workspaceId,
          automation: automationId,
          data: {
            trigger_id: pollInstanceId,
            input_mapping: inputMappings,
            phase_type: V1PhaseType.PHASE_TYPE_PRE,
          },
          params: {
            stage: isDraft
              ? CreateAutomationTriggerStage.AUTOMATION_STAGE_DRAFT
              : CreateAutomationTriggerStage.AUTOMATION_STAGE_PUBLISHED,
          },
        });

        // Invalidate both Grimoire and BDK trigger queries to refresh the list
        queryClient.invalidateQueries({
          queryKey: getListAutomationTriggersQueryKey(
            organizationId,
            workspaceId,
            automationId,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: getRetrieveWorkspacePollInstancesQueryKey(
            organizationId,
            workspaceId,
          ),
        });

        // Show success toast and call onSuccess callback
        toast.success(toastMessages.success);
        onSuccess?.();
      } catch (error) {
        const defaultTags: TSentryTags = {
          component: 'useCreateTrigger',
          action: 'createTrigger',
        };
        captureException(error as Error, {
          tags: sentryTags ?? defaultTags,
        });
        toast.error(toastMessages.error);
      }
    },
    [
      organizationId,
      workspaceId,
      automationId,
      isDraft,
      addPollInstance,
      setAutomationsForPollInstance,
      createAutomationTrigger,
      queryClient,
      toastMessages,
      sentryTags,
      onSuccess,
    ],
  );

  return {
    createTrigger,
    isLoading:
      isAddingPollInstance ||
      isSettingAutomations ||
      isCreatingAutomationTrigger,
  };
}
